import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Input, Select, Toast, Popconfirm, Form, Tag } from '@douyinfe/semi-ui';
import { IconEdit, IconDelete, IconPlus, IconSearch } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import styles from './index.module.scss';
import FieldRuleForm from '../../components/FieldRuleForm';

const statusMap = {
    0: { text: '正常', type: 'success' },
    1: { text: '隐藏', type: 'danger' },
};

const typMap = {
    1: '用户信息',
    2: '用户信用',
    10: '用户资料',
    20: '员工资料',
};

const parseRule = (rule) => {
    try {
        return JSON.parse(rule);
    } catch {
        return {};
    }
};

// 字段类型映射
const fieldTypeMap = {
    string: '文本',
    number: '数字',
    select: '单选',
    mselect: '多选',
    img: '图片',
    date: '日期',
    time: '时间',
    datetime: '日期时间',
    phone: '手机号',
    range: '区间',
    addr: '地址',
};

// 时间格式化函数
const formatTime = (val) => {
    if (!val) return '-';
    // 如果是纯数字且长度大于等于10位，认为是时间戳
    if (/^\d{10,}$/.test(val)) {
        const d = new Date(Number(val));
        if (!isNaN(d.getTime())) {
            return d.toLocaleString();
        }
    }
    return val;
};

const AllFields = () => {
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [searchTyp, setSearchTyp] = useState(null);
    const [typList, setTypList] = useState([]);
    const [formApi, setFormApi] = useState(null);

    useEffect(() => {
        const fetchTypList = async () => {
            try {
                const response = await request.get('/go/sp/all/list_all_field_typ/');
                if (response.errno === 0) {
                    setTypList(response.list || []);
                } else {
                    Toast.error(response.errstr || '获取分类失败');
                }
            } catch (error) {
                Toast.error('获取分类失败');
            }
        };
        fetchTypList();
    }, []);

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/all/list_all_field/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus,
                    typ: searchTyp === null ? -1 : searchTyp
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, [searchTyp, searchStatus]);

    const handleAdd = async (values) => {
        try {
            const processedValues = {
                ...values,
                typ: Number(values.typ),
                rule: JSON.stringify(values.rule)
            };
            const response = await request.post('/go/sp/all/add_all_field/', processedValues);
            if (response.errno === 0) {
                Toast.success('添加成功');
                setShowAddModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '添加失败');
            }
        } catch (error) {
            Toast.error('添加失败');
        }
    };

    const handleEdit = async (values) => {
        try {
            const processedValues = {
                ...values,
                typ: Number(values.typ),
                rule: JSON.stringify(values.rule),
                uid: currentRecord.uid
            };
            const response = await request.post('/go/sp/all/edit_all_field/', processedValues);
            if (response.errno === 0) {
                Toast.success('编辑成功');
                setShowEditModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '编辑失败');
            }
        } catch (error) {
            Toast.error('编辑失败');
        }
    };

    const handleDelete = async (uids) => {
        try {
            const response = await request.post('/go/sp/all/delete_all_field/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (response.errno === 0) {
                Toast.success('删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '删除失败');
            }
        } catch (error) {
            Toast.error('删除失败');
        }
    };

    const handleBatchEdit = async (status, uids) => {
        if (!uids || (Array.isArray(uids) && !uids.length)) {
            Toast.warning('请选择要操作的字段');
            return;
        }
        try {
            const response = await request.post('/go/sp/all/batchedit_all_field/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids,
                status
            });
            if (response.errno === 0) {
                Toast.success('操作成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '操作失败');
            }
        } catch (error) {
            Toast.error('操作失败');
        }
    };

    const columns = [
        {
            title: '分类',
            dataIndex: 'typ',
            render: (typ) => typList.find(item => item.uid == typ)?.name || typMap[typ] || '-',
        },
        {
            title: '名称',
            dataIndex: 'rule_name',
            render: (text, record) => parseRule(record.rule).n || '-',
        },
        {
            title: '唯一标识',
            dataIndex: 'ukey',
        },
        {
            title: '类型',
            dataIndex: 'rule_type',
            render: (text, record) => fieldTypeMap[parseRule(record.rule).t] || parseRule(record.rule).t || '-',
        },
        {
            title: '必填',
            dataIndex: 'rule_required',
            render: (text, record) => parseRule(record.rule).r ? <Tag color='orange'>必填</Tag> : '-',
        },
        {
            title: '字段属性',
            dataIndex: 'rule_props',
            render: (text, record) => {
                const rule = parseRule(record.rule);
                return (
                    <div>
                        {rule.d ? <div><b>默认值：</b>{rule.d}</div> : null}
                        {rule.p ? <div><b>校验规则：</b>{rule.p}</div> : null}
                        {rule.l ? <div><b>选项：</b>{rule.l}</div> : null}
                        {rule.u ? <div><b>唯一：</b><Tag color='blue'>唯一</Tag></div> : null}
                    </div>
                );
            }
        },
        {
            title: '排序',
            dataIndex: 'sort',
        },
        {
            title: '状态',
            dataIndex: 'status',
            render: (text) => {
                const status = statusMap[text] || { text: '异常', type: 'danger' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '创建/更新时间',
            dataIndex: 'update_time',
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTime(record.create_time)}</div>
                    <div>{formatTime(text)}</div>
                </div>
            )
        },
        {
            title: '操作',
            width: 180,
            render: (_, record) => (
                <Space>
                    <Button
                        type='primary'
                        icon={<IconEdit />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowEditModal(true);
                        }}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除该字段吗？"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button type='danger' icon={<IconDelete />}>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const renderForm = (type = 'add') => {
        const isEdit = type === 'edit';
        const initialValues = isEdit && currentRecord ? {
            ...currentRecord,
            typ: String(currentRecord.typ),
            rule: typeof currentRecord.rule === 'string' ? parseRule(currentRecord.rule) : (currentRecord.rule || {})
        } : {
            typ: '',
            ukey: '',
            rule: { n: '', r: false, t: '', d: '', p: '', l: '', u: false },
            sort: 0,
            status: 0
        };
        const [formValues, setFormValues] = useState(initialValues);

        useEffect(() => {
            setFormValues(initialValues);
        }, [showAddModal, showEditModal, currentRecord]);

        const handleFormChange = (values) => {
            setFormValues(values);
        };

        return (
            <Form
                getFormApi={setFormApi}
                initValues={initialValues}
                onValueChange={handleFormChange}
                onSubmit={values => {
                    if (isEdit) {
                        handleEdit(values);
                    } else {
                        handleAdd(values);
                    }
                }}
                style={{ padding: '20px' }}
            >
                <Form.Select
                    field="typ"
                    label="字段分类"
                    style={{ width: '100%' }}
                    rules={[{ required: true, message: '请选择字段分类' }]}
                >
                    {typList.map((item) => (
                        <Select.Option key={item.uid} value={item.uid}>{item.name}</Select.Option>
                    ))}
                </Form.Select>
                <Form.Input
                    field="ukey"
                    label="唯一标识"
                    rules={[{ required: true, message: '请输入唯一标识' }]}
                />
                <FieldRuleForm fieldPrefix="rule" typeValue={formValues?.rule?.t} />
                <Form.InputNumber
                    field="sort"
                    label="排序"
                    min={0}
                    style={{ width: '100%' }}
                />
                <Form.Select
                    field="status"
                    label="状态"
                    style={{ width: '100%' }}
                >
                    {Object.entries(statusMap).map(([value, { text }]) => (
                        <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                    ))}
                </Form.Select>
                <div style={{ marginTop: 24, textAlign: 'right' }}>
                    <Space>
                        <Button type='tertiary' onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}>取消</Button>
                        <Button type='primary' htmlType="submit">确定</Button>
                    </Space>
                </div>
            </Form>
        );
    };

    return (
        <div className={styles.articlesContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索字段名称/唯一标识"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Select
                        placeholder="字段状态"
                        value={searchStatus}
                        onChange={setSearchStatus}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部状态</Select.Option>
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    <Select
                        placeholder="字段分类"
                        value={searchTyp}
                        onChange={setSearchTyp}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部分类</Select.Option>
                        {typList.map((item) => (
                            <Select.Option key={item.uid} value={item.uid}>{item.name}</Select.Option>
                        ))}
                    </Select>
                    <Button type='primary' icon={<IconSearch />} onClick={() => fetchList(1, pagination.pageSize)}>搜索</Button>
                    <Button type='primary' icon={<IconPlus />} onClick={() => setShowAddModal(true)}>新增字段</Button>
                    {selectedRowKeys.length > 0 && (
                        <>
                            <Button
                                type='warning'
                                onClick={() => handleBatchEdit(1, selectedRowKeys)}
                            >批量隐藏</Button>
                            <Button
                                type='success'
                                onClick={() => handleBatchEdit(0, selectedRowKeys)}
                            >批量显示</Button>
                            <Popconfirm
                                title="确定要批量删除选中字段吗？"
                                onConfirm={() => handleDelete(selectedRowKeys)}
                            >
                                <Button type='danger'>批量删除</Button>
                            </Popconfirm>
                        </>
                    )}
                </Space>
            </div>
            <Table
                rowKey={record => record.uid ? String(record.uid) : `${record.ukey}-${record.typ}`}
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onChange: (page, pageSize) => fetchList(page, pageSize)
                }}
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys
                }}
                style={{ marginTop: 16 }}
            />
            <Modal
                title="新增字段"
                visible={showAddModal}
                onCancel={() => setShowAddModal(false)}
                footer={null}
                width={600}
            >
                {renderForm('add')}
            </Modal>
            <Modal
                title="编辑字段"
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={null}
                width={600}
            >
                {renderForm('edit')}
            </Modal>
        </div>
    );
};

export default AllFields; 